import {User} from './auth';
import {Resource, ResourceOption} from './resources.datatypes';
import {PaymentSchedule} from './payment_schedule.datatypes';
import {BillingParty} from './billing_party.datatypes';
import {GroupMemberPayment} from './payments.datatypes';
import {WcvDocument} from './documents.datatypes';

export interface RoomPartyOption {
  __typename?: string | undefined
  id: number
  room_party_id: number
  group_resource_option_id: number
  unit_count: number
  unit_cost: number
  admin_edited: boolean

  roomParty?: RoomParty
  groupResourceOption?: GroupResourceOption
}

export interface RoomPartyMemberOption {
  __typename?: string | undefined
  id: number
  room_party_member_id: number
  group_resource_option_id: number
  unit_count: number
  unit_cost: number
  admin_edited: boolean
  deleted_at: Date | string

  roomPartyMember?: RoomPartyMember
  groupResourceOption?: GroupResourceOption
}

export interface RoomPartyRoomResource {
  id: number
  room_party_id: number
  room_resource_id: number

  roomParty: RoomParty
  resource: Resource
}

export interface RoomParty {
  __typename?: string | undefined
  id: number
  group_booking_id?: number
  hotel_resource_id?: number | null
  disabled?: boolean
  start_date?: Date | string
  end_date?: Date | string
  wheelchair_access?: boolean
  has_infants?: boolean
  notes?: string
  members_different_dates?: boolean

  booking?: GroupBooking
  hotel?: Resource
  roomResource?: RoomPartyRoomResource
  members?: RoomPartyMember[]
  options?: RoomPartyOption[]
}

export interface RoomPartyWithoutPrimaryOccupant {
    __typename?: string | undefined
    id: number
    group_name: string
    primary_occupant: string
    date_booked: Date
    members_count: number
}

export interface RoomPartyMember {
  __typename?: string | undefined,
  id: number
  room_party_id?: number
  group_member_id?: number
  billing_party_id?: number
  primary_occupant?: boolean
  guest_number?: number
  start_date?: Date | string
  end_date?: Date | string
  cancelled?: boolean

  cost?: number
  totalPayments?: number
  outstandingBalance?: number
  hasVaryingDates?: boolean

  roomParty?: RoomParty
  groupMember?: GroupMember
  billingParty?: BillingParty
  options?: RoomPartyMemberOption[]
}

export interface GroupBooking {
  __typename?: string | undefined,
  id: number
  group_id: number
  soft_cancelled: boolean
  disabled: boolean,
  start_date?: Date | string
  end_date?: Date | string
  created_at?: Date
  admin_locked?: boolean

  cost?: number
  outstandingBalance?: number
  overduePayments?: number

  group?: Group
  roomParties?: RoomParty[]
  billingParties?: BillingParty[]
}

export interface GroupResourceOption {
  __typename?: string | undefined,
  id: number
  group_id?: number
  resource_option_id?: number
  mandatory?: boolean
  editing_level?: number
  view_level?: number
  groupOption?: number
  unit_cost_override?: number
  action_type_override?: number
  hidden_override?: boolean
  additional_charge_override?: boolean

  unitCost?: number
  unit_cost?: number
  action_type?: number
  is_hidden?: boolean
  is_additional_charge?: boolean

  group: Group
  option: ResourceOption

  roomMemberOptions?: RoomPartyMemberOption[]
  roomOptions?: RoomPartyOption[]
}

export interface GroupMemberAdjustment {
  __typename?: string | undefined,
  id: number
  group_member_id?: number
  adjustment_type?: number
  amount?: number
  description?: string
}

export interface GroupMember {
  __typename?: string | undefined,
  id: number
  group_id?: number
  user_id?: number

  first_name: string
  last_name: string
  group_role: number
  tour_role: number
  email?: string
  cellphone_code?: string
  cellphone?: string
  dob?: Date | string
  validated?: boolean
  cancelled?: boolean
  disabled?: boolean
  authorised?: boolean
  ageAtEvent?: number

  group?: Group
  user?: User
  roomMember?: RoomPartyMember
  groupMemberPayments?: GroupMemberPayment[]
  adjustments?: GroupMemberAdjustment[]
  documents?: WcvDocument[]
}

export interface Group {
    __typename: string | undefined,
    id: number
    tour_resource_id?: number
    hotel_resource_id?: number
    name?: string
    description?: string
    description_resort?: string
    description_whats_included?: string
    description_checkin?: string
    editing_level?: number
    start_date?: Date | string
    start_date_additional?: Date | string
    end_date?: Date | string
    end_date_additional?: Date | string
    max_additional_nights?: number
    booking_form_url?: string
    invoice_document_id?: number
    convenience_fee?: number
    credit_card_fee?: number
    totalCreditCardFees?: number
    comp_dollar_value?: number
    comp_ratio?: number
    comp_dollar_estimate?: number
    comp_final_value?: number
    admissions_cutoff?: Date | string
    default_authorize_members?: boolean
    validated?: boolean
    disabled?: boolean
    locked?: boolean
    booking_tool_edit_cutoff?: Date | string
    description_insurance?: string

    hasPrimaryLeader?: boolean
    hasLeader?: boolean

    cancelled_members_count?: number
    members_count?: number
    performers_count?: number

    cost?: number
    outstandingBalance?: number
    overduePayments?: number

    primaryLeader?: GroupMember,
    tour?: Resource,
    hotel?: Resource
    members?: GroupMember[]
    active_members?: GroupMember[]
    associatedUsers?: User[]
    options?: GroupResourceOption[]
    optionIds?: number[]
    bookings?: GroupBooking[]
    payment_schedule?: PaymentSchedule
    documents?: WcvDocument[]
}

export interface GroupOptionsOverview {
    id: number
    display_name: string
    internal_name: string
    room_member_options_count: number
    room_options_count: number
    total_options_count: number
}

export const GroupMemberRoleType = {
  MEMBER: 0,
  PRIMARY_LEADER: 1,
  LEADER: 2,
};

export const GroupMemberRoleTypes = [
  {name: 'member'},
  {name: 'primary leader'},
  {name: 'leader'},
];

export const GroupEditingLevelType = {
  WCV_ONLY: 0,
  THIRD_PARTY: 1,
  PRIMARY_LEADER: 2,
  ALL_LEADERS: 3,
  PRIMARY_PAYING_MEMBER: 4,
  ALL_PAYING_MEMBERS: 5,
};

export const GroupEditingLevelTypes = [
  {name: 'WCV Only'},
  {name: 'WCV + 3rd Part Admin'},
  {name: 'WCV + 3rd Part Admin + Primary Leader'},
  {name: 'WCV + 3rd Part Admin + All Leaders'},
  {name: 'WCV + 3rd Part Admin + All Leaders + Primary Paying Member'},
  {name: 'WCV + 3rd Part Admin + All Leaders + All Paying Members'},
];

export const TourRoleType = {
  TOUR_ROLE_FAMILY_AND_FRIENDS: 0,
  TOUR_ROLE_PERFORMER: 1,
  TOUR_ROLE_STUDENT: 2,
  TOUR_ROLE_CHAPERONE: 3,
};

export const TourRoleTypes = [
  {
    name: 'Family & Friends',
    hint: ''
  },
  {
    name: 'Performer',
    hint: 'A person performing in the event.'
  },
  {
    name: 'Student',
    hint: ''
  },
  {
    name: 'Chaperone',
    hint: ''
  },
];

export const AdjustmentType = {
  DISCOUNT: 0,
  ADDITIONAL_COST: 1,
  CREDIT_CARD_FEE: 2,
};

export const AdjustmentTypes = [
  {name: 'discount'},
  {name: 'additional cost'},
  {name: 'credit card fee'},
];
